import { Modal, DatePicker, TimePicker, Checkbox, Button } from "antd";
import { Copy, Truck, Users, Package, Calendar, Clock } from "lucide-react";
import { useState } from "react";
import { TServiceTicket } from "~/types";
import dayjs, { Dayjs } from "dayjs";

interface DuplicateServiceModalProps {
  ticket: TServiceTicket | null;
  open: boolean;
  onClose: () => void;
  onDuplicate: (options: DuplicateOptions) => void;
}

export interface DuplicateOptions {
  includeVessel: boolean;
  includeStaff: boolean;
  includeInventory: boolean;
  serviceDate: Dayjs | null;
  startTime: Dayjs | null;
}

export const DuplicateServiceModal: React.FC<DuplicateServiceModalProps> = ({
  ticket,
  open,
  onClose,
  onDuplicate,
}) => {
  const [includeVessel, setIncludeVessel] = useState(true);
  const [includeStaff, setIncludeStaff] = useState(true);
  const [includeInventory, setIncludeInventory] = useState(true);
  const [serviceDate, setServiceDate] = useState<Dayjs | null>(dayjs());
  const [startTime, setStartTime] = useState<Dayjs | null>(dayjs().hour(9).minute(0));

  const handleDuplicate = () => {
    onDuplicate({
      includeVessel,
      includeStaff,
      includeInventory,
      serviceDate,
      startTime,
    });
    // Reset state after duplicating
    resetState();
  };

  const resetState = () => {
    setIncludeVessel(true);
    setIncludeStaff(true);
    setIncludeInventory(true);
    setServiceDate(dayjs());
    setStartTime(dayjs().hour(9).minute(0));
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  if (!ticket) return null;

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <Copy className="text-blue-600" size={20} />
          <span>Duplicate Service Ticket</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={500}
    >
      <div className="py-4">
        {/* Original Ticket Info */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg border">
          <div className="text-sm text-gray-500 mb-1">Duplicating from:</div>
          <div className="font-medium">
            {ticket.serial} - {ticket.serviceType?.replace(/_/g, " ")}
          </div>
          {ticket.serviceDatetime && (
            <div className="text-sm text-gray-600">
              Original date: {dayjs(ticket.serviceDatetime).format("MMM D, YYYY")}
            </div>
          )}
        </div>

        {/* New Service Date & Time */}
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 mb-3">
            New Service Date & Time
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                <Calendar size={12} className="inline mr-1" />
                Service Date
              </label>
              <DatePicker
                value={serviceDate}
                onChange={setServiceDate}
                className="w-full"
                format="MMM D, YYYY"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                <Clock size={12} className="inline mr-1" />
                Start Time
              </label>
              <TimePicker
                value={startTime}
                onChange={setStartTime}
                className="w-full"
                format="HH:mm"
                minuteStep={15}
              />
            </div>
          </div>
        </div>

        {/* Copy Options */}
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 mb-3">
            Include in duplicate:
          </div>
          <div className="space-y-3">
            {/* Vessel Option */}
            <div
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                includeVessel ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
              }`}
              onClick={() => setIncludeVessel(!includeVessel)}
            >
              <Checkbox checked={includeVessel} onChange={(e) => setIncludeVessel(e.target.checked)}>
                <div className="flex items-center space-x-2 ml-1">
                  <Truck size={16} className={includeVessel ? "text-blue-600" : "text-gray-400"} />
                  <div>
                    <div className="font-medium">Same Vessel</div>
                    <div className="text-xs text-gray-500">
                      {ticket.vessel?.name || "No vessel assigned"}
                    </div>
                  </div>
                </div>
              </Checkbox>
            </div>

            {/* Staff Option */}
            <div
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                includeStaff ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
              }`}
              onClick={() => setIncludeStaff(!includeStaff)}
            >
              <Checkbox checked={includeStaff} onChange={(e) => setIncludeStaff(e.target.checked)}>
                <div className="flex items-center space-x-2 ml-1">
                  <Users size={16} className={includeStaff ? "text-blue-600" : "text-gray-400"} />
                  <div>
                    <div className="font-medium">Same Staff</div>
                    <div className="text-xs text-gray-500">
                      {ticket.staffs && ticket.staffs.length > 0
                        ? `${ticket.staffs.length} staff member${ticket.staffs.length > 1 ? "s" : ""}`
                        : "No staff assigned"}
                    </div>
                  </div>
                </div>
              </Checkbox>
            </div>

            {/* Inventory Option */}
            <div
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                includeInventory ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
              }`}
              onClick={() => setIncludeInventory(!includeInventory)}
            >
              <Checkbox checked={includeInventory} onChange={(e) => setIncludeInventory(e.target.checked)}>
                <div className="flex items-center space-x-2 ml-1">
                  <Package size={16} className={includeInventory ? "text-blue-600" : "text-gray-400"} />
                  <div>
                    <div className="font-medium">Same Inventory</div>
                    <div className="text-xs text-gray-500">
                      {ticket.inventories && ticket.inventories.length > 0
                        ? `${ticket.inventories.length} item${ticket.inventories.length > 1 ? "s" : ""}`
                        : "No inventory assigned"}
                    </div>
                  </div>
                </div>
              </Checkbox>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="text-sm text-yellow-800">
            The duplicate will be created as a <strong>new service ticket</strong> with status "Scheduled".
            You can review and modify all details before saving.
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleDuplicate}
            className="bg-blue-600 hover:bg-blue-700"
            icon={<Copy size={14} />}
          >
            Continue to Form
          </Button>
        </div>
      </div>
    </Modal>
  );
};
